import React, { useEffect, useState } from "react";
import { collection, doc, getDocs, onSnapshot, orderBy, query, serverTimestamp, setDoc, where } from "firebase/firestore";
import toast from "react-hot-toast";
import { db, firebaseProjectId } from "../../firebase";
import { downloadCsv } from "../../utils/csvExport";

export function OperationsTab() {
  const [loading, setLoading] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const [announcementBanner, setAnnouncementBanner] = useState("");
  const [announcementStart, setAnnouncementStart] = useState("");
  const [announcementEnd, setAnnouncementEnd] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const ref = doc(db, "app_config", "panel");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const d = snap.data() || {};
        setMaintenanceMode(!!d.maintenanceMode);
        setMaintenanceMessage(String(d.maintenanceMessage || ""));
        setAnnouncementBanner(String(d.announcementBanner || ""));
        const s = d.announcementStart;
        const e = d.announcementEnd;
        if (s?.toDate) setAnnouncementStart(s.toDate().toISOString().slice(0, 16));
        else setAnnouncementStart("");
        if (e?.toDate) setAnnouncementEnd(e.toDate().toISOString().slice(0, 16));
        else setAnnouncementEnd("");
        setLoading(false);
      },
      (err) => {
        console.error(err);
        toast.error("Could not load app_config (check Firestore rules).");
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        maintenanceMode,
        maintenanceMessage: maintenanceMessage.trim(),
        announcementBanner: announcementBanner.trim(),
        updatedAt: serverTimestamp(),
      };
      if (announcementStart) {
        payload.announcementStart = new Date(announcementStart);
      } else {
        payload.announcementStart = null;
      }
      if (announcementEnd) {
        payload.announcementEnd = new Date(announcementEnd);
      } else {
        payload.announcementEnd = null;
      }
      await setDoc(doc(db, "app_config", "panel"), payload, { merge: true });
      toast.success("Saved app configuration");
    } catch (e) {
      console.error(e);
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500">
        Loading configuration…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900">Feature flags</h2>
        <p className="mt-1 text-sm text-slate-500">
          Stored in <code className="rounded bg-slate-100 px-1">app_config/panel</code>. Maintenance blocks signed-in
          non-admin users until disabled.
        </p>
        <label className="mt-4 flex cursor-pointer items-center gap-3">
          <input
            type="checkbox"
            checked={maintenanceMode}
            onChange={(e) => setMaintenanceMode(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-indigo-600"
          />
          <span className="text-sm font-medium text-slate-800">Maintenance mode</span>
        </label>
        <div className="mt-3">
          <label className="mb-1 block text-xs font-medium text-slate-600">Maintenance message</label>
          <textarea
            value={maintenanceMessage}
            onChange={(e) => setMaintenanceMessage(e.target.value)}
            rows={2}
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Shown on the maintenance screen"
          />
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900">Announcements</h2>
        <p className="mt-1 text-sm text-slate-500">Banner text for all users (respects optional start/end).</p>
        <div className="mt-4 space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Banner text</label>
            <input
              value={announcementBanner}
              onChange={(e) => setAnnouncementBanner(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Show from (local)</label>
              <input
                type="datetime-local"
                value={announcementStart}
                onChange={(e) => setAnnouncementStart(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Show until (local)</label>
              <input
                type="datetime-local"
                value={announcementEnd}
                onChange={(e) => setAnnouncementEnd(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              />
            </div>
          </div>
        </div>
      </section>

      <button
        type="button"
        disabled={saving}
        onClick={save}
        className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save operations"}
      </button>
    </div>
  );
}

export function HealthTab({ getCollectionCounts }) {
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getCollectionCounts({
          collections: [
            "users",
            "orders",
            "customer_entries",
            "purchaseOrders",
            "auditLogs",
            "sales_import_months",
            "items",
            "holidays",
            "payrollHalfDayMarks",
          ],
        });
        if (!cancelled) setCounts(res.data?.counts || {});
      } catch (e) {
        console.error(e);
        toast.error("Could not load collection counts");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getCollectionCounts]);

  const backupUrl = `https://console.firebase.google.com/project/${firebaseProjectId}/settings/backups`;

  const rows = [
    { key: "users", label: "Users" },
    { key: "orders", label: "Orders" },
    { key: "customer_entries", label: "Customer entries" },
    { key: "purchaseOrders", label: "Purchase orders" },
    { key: "auditLogs", label: "Audit logs" },
    { key: "sales_import_months", label: "Sales import months" },
    { key: "items", label: "Items" },
    { key: "holidays", label: "Holidays (company + cash leave)" },
    { key: "payrollHalfDayMarks", label: "Payroll half-day marks" },
  ];

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900">Firestore usage (read-only)</h2>
        <p className="mt-1 text-sm text-slate-500">Approximate document counts via secured Cloud Function.</p>
        {loading ? (
          <p className="mt-4 text-sm text-slate-500">Loading…</p>
        ) : (
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {rows.map((r) => (
              <li
                key={r.key}
                className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50/80 px-4 py-2 text-sm"
              >
                <span className="text-slate-700">{r.label}</span>
                <span className="font-mono tabular-nums text-slate-900">
                  {counts[r.key] >= 0 ? counts[r.key].toLocaleString() : "—"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-2xl border border-amber-100 bg-amber-50/80 p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-amber-950">Backups</h2>
        <p className="mt-2 text-sm text-amber-900/90">
          Schedule exports and point-in-time recovery in the Firebase console. This link opens project backups settings.
        </p>
        <a
          href={backupUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-flex rounded-xl bg-amber-200/80 px-4 py-2 text-sm font-semibold text-amber-950 hover:bg-amber-200"
        >
          Open Firebase backup settings
        </a>
      </section>
    </div>
  );
}

const ENTITY_PRESETS = ["user", "order", "purchaseOrder", "customer_entry"];

function formatAuditTime(t) {
  if (!t) return "—";
  if (typeof t?.toDate === "function") return t.toDate().toLocaleString();
  if (typeof t?.seconds === "number") return new Date(t.seconds * 1000).toLocaleString();
  if (typeof t?._seconds === "number") return new Date(t._seconds * 1000).toLocaleString();
  return "—";
}

export function AuditTab({ queryAuditLogs }) {
  const [entity, setEntity] = useState("");
  const [days, setDays] = useState(14);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("function");

  const loadAuditLogsFromFirestore = async ({ entity: ent, days: d, limitRows = 200 }) => {
    const from = new Date();
    from.setDate(from.getDate() - Math.max(1, Number(d) || 14));
    const clauses = [where("timestamp", ">=", from), orderBy("timestamp", "desc")];
    if (ent) clauses.unshift(where("entity", "==", ent));
    const qy = query(collection(db, "auditLogs"), ...clauses);
    const snap = await getDocs(qy);
    return snap.docs.slice(0, limitRows).map((x) => ({ id: x.id, ...(x.data() || {}) }));
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await queryAuditLogs({
        entity: entity || undefined,
        days,
        limit: 200,
      });
      setRows(res.data?.rows || []);
      setSource("function");
    } catch (e) {
      console.error(e);
      try {
        const fallbackRows = await loadAuditLogsFromFirestore({ entity, days, limitRows: 200 });
        setRows(fallbackRows);
        setSource("firestore");
        toast("Loaded via Firestore fallback (function unavailable).", { icon: "⚠️" });
      } catch (fallbackErr) {
        console.error(fallbackErr);
        toast.error("Failed to load audit logs");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load on mount
  }, []);

  const exportCsv = () => {
    downloadCsv(
      `audit-export-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        { key: "timestamp", header: "timestamp" },
        { key: "entity", header: "entity" },
        { key: "entityId", header: "entityId" },
        { key: "action", header: "action" },
        { key: "byEmail", header: "byEmail" },
        { key: "role", header: "role" },
      ],
      rows.map((r) => ({
        ...r,
        timestamp:
          r.timestamp?.toDate?.()?.toISOString?.() ||
          (r.timestamp?.seconds ? new Date(r.timestamp.seconds * 1000).toISOString() : "") ||
          (r.timestamp?._seconds ? new Date(r.timestamp._seconds * 1000).toISOString() : ""),
      }))
    );
    toast.success("Exported CSV");
  };

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900">Audit log viewer</h2>
        <p className="mt-1 text-sm text-slate-500">
          Recent entries from <code className="rounded bg-slate-100 px-1">auditLogs</code> (filtered in Cloud Function).
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Source: <strong>{source === "function" ? "Cloud Function" : "Firestore fallback"}</strong>
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Entity (optional)</label>
            <select
              value={entity}
              onChange={(e) => setEntity(e.target.value)}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="">All</option>
              {ENTITY_PRESETS.filter(Boolean).map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Days</label>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
            >
              {[7, 14, 30, 60, 90].map((d) => (
                <option key={d} value={d}>
                  {d} days
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={load}
            className="rounded-xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-900"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={exportCsv}
            disabled={!rows.length}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-40"
          >
            Export CSV
          </button>
        </div>
      </section>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="max-h-[480px] overflow-auto">
          <table className="w-full min-w-[640px] text-left text-xs">
            <thead className="sticky top-0 bg-slate-50 text-[10px] font-semibold uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Time</th>
                <th className="px-3 py-2">Entity</th>
                <th className="px-3 py-2">Action</th>
                <th className="px-3 py-2">By</th>
                <th className="px-3 py-2">Role</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-slate-500">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-slate-500">
                    No rows in this range.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50/80">
                    <td className="whitespace-nowrap px-3 py-2 text-slate-600">{formatAuditTime(r.timestamp)}</td>
                    <td className="px-3 py-2">
                      {r.entity || "—"} {r.entityId ? <span className="text-slate-400">#{r.entityId}</span> : null}
                    </td>
                    <td className="px-3 py-2">{r.action || "—"}</td>
                    <td className="max-w-[180px] truncate px-3 py-2">{r.byEmail || r.by || "—"}</td>
                    <td className="px-3 py-2">{r.role || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
